import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Mail, Phone, Calendar, Package, Eye, Trash2, Search, Download, X, DollarSign, Link as LinkIcon, CheckCircle } from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';

const API = process.env.REACT_APP_BACKEND_URL;

const AdminEnquiries = () => {
  const navigate = useNavigate();
  const [enquiries, setEnquiries] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterStatus, setFilterStatus] = useState('all');
  const [selectedEnquiry, setSelectedEnquiry] = useState(null);

  useEffect(() => {
    const token = localStorage.getItem('adminToken');
    if (!token) {
      navigate('/admin');
      return;
    }
    fetchEnquiries();
  }, [navigate]);

  const fetchEnquiries = async () => {
    try {
      const token = localStorage.getItem('adminToken');
      const response = await axios.get(`${API}/api/admin/enquiries`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      if (response.data.success) {
        setEnquiries(response.data.enquiries);
      }
      setLoading(false);
    } catch (error) {
      console.error('Fetch error:', error);
      if (error.response?.status === 401) {
        toast.error('Session expired. Please login again.');
        localStorage.removeItem('adminToken');
        navigate('/admin');
      } else {
        toast.error('Failed to load enquiries');
      }
      setLoading(false);
    }
  };

  const createTestOrder = async () => {
    try {
      const token = localStorage.getItem('adminToken');
      const res = await axios.post(`${API}/api/admin/create-test-order`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (res.data.success) {
        const orderId = res.data.orderId;
        toast.success(`Test order ${orderId} created - opening upload page…`);
        fetchEnquiries();
        setTimeout(() => window.open(`/upload/${orderId}`, '_blank'), 800);
      }
    } catch (e) {
      console.error(e);
      toast.error('Failed to create test order');
    }
  };

  const bulkDeleteTestOrders = async () => {
    if (!window.confirm('Delete ALL test orders? This cannot be undone.')) return;
    try {
      const token = localStorage.getItem('adminToken');
      const res = await axios.delete(`${API}/api/admin/enquiries/bulk/test`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (res.data.success) {
        toast.success(res.data.message);
        fetchEnquiries();
      }
    } catch (e) {
      console.error(e);
      toast.error('Failed to delete test orders');
    }
  };

  const handleDelete = async (enquiryId) => {
    if (!window.confirm('Are you sure you want to delete this enquiry? This action cannot be undone.')) {
      return;
    }

    try {
      const token = localStorage.getItem('adminToken');
      await axios.delete(`${API}/api/admin/enquiries/${enquiryId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      toast.success('Enquiry deleted successfully');
      // Remove from local state
      setEnquiries(enquiries.filter(e => e.id !== enquiryId));
    } catch (error) {
      console.error('Delete error:', error);
      toast.error('Failed to delete enquiry');
    }
  };

  const handleStatusChange = async (enquiryId, newStatus) => {
    try {
      const token = localStorage.getItem('adminToken');
      await axios.patch(`${API}/api/admin/enquiries/${enquiryId}`, 
        { status: newStatus },
        { headers: { Authorization: `Bearer ${token}` }}
      );
      
      toast.success('Status updated successfully');
      // Update local state
      setEnquiries(enquiries.map(e => 
        e.id === enquiryId ? { ...e, status: newStatus } : e
      ));
    } catch (error) {
      console.error('Update error:', error);
      toast.error('Failed to update status');
    }
  };

  const handleStageChange = async (enquiryId, newStage) => {
    try {
      const token = localStorage.getItem('adminToken');
      const stageInt = parseInt(newStage, 10);
      await axios.patch(`${API}/api/admin/enquiries/${enquiryId}`,
        { production_stage: stageInt },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success('Production stage updated');
      setEnquiries(enquiries.map(e =>
        e.id === enquiryId ? { ...e, production_stage: stageInt } : e
      ));
      if (selectedEnquiry && selectedEnquiry.id === enquiryId) {
        setSelectedEnquiry({ ...selectedEnquiry, production_stage: stageInt });
      }
    } catch (error) {
      console.error('Stage update error:', error);
      toast.error('Failed to update production stage');
    }
  };

  const createFinalPaymentLink = async (enquiry) => {
    try {
      const token = localStorage.getItem('adminToken');
      const response = await axios.post(`${API}/api/admin/create-final-payment-link`,
        { enquiry_id: enquiry.id },
        { headers: { Authorization: `Bearer ${token}` }}
      );

      if (response.data.success) {
        toast.success(`Payment link created for £${response.data.amount.toFixed(2)}`);
        
        // Update local state
        setEnquiries(enquiries.map(e => 
          e.id === enquiry.id 
            ? { ...e, final_payment_link: response.data.payment_link, final_payment_status: 'pending' } 
            : e
        ));

        // Copy to clipboard
        navigator.clipboard.writeText(response.data.payment_link);
        toast.info('Payment link copied to clipboard!');
      }
    } catch (error) {
      console.error('Payment link error:', error);
      toast.error('Failed to create payment link');
    }
  };

  const markAsPaid = async (enquiryId) => {
    if (!window.confirm('Mark this order as PAID IN FULL? This confirms the customer has paid the remaining balance.')) {
      return;
    }

    try {
      const token = localStorage.getItem('adminToken');
      await axios.post(`${API}/api/admin/mark-paid/${enquiryId}`,
        {},
        { headers: { Authorization: `Bearer ${token}` }}
      );

      toast.success('Marked as paid in full!');
      
      // Update local state
      setEnquiries(enquiries.map(e => 
        e.id === enquiryId ? { ...e, payment_status: 'paid_in_full', final_payment_status: 'completed' } : e
      ));
    } catch (error) {
      console.error('Mark paid error:', error);
      toast.error('Failed to update payment status');
    }
  };

  const resendPaymentEmail = async (enquiry) => {
    try {
      const token = localStorage.getItem('adminToken');
      await axios.post(`${API}/api/admin/resend-payment-email/${enquiry.id}`,
        {},
        { headers: { Authorization: `Bearer ${token}` }}
      );

      toast.success(`Payment email resent to ${enquiry.email}`);
    } catch (error) {
      console.error('Resend email error:', error);
      toast.error(error.response?.data?.detail || 'Failed to resend email');
    }
  };

  const sendReviewRequest = async (enquiry) => {
    if (!window.confirm(`Send a review request email to ${enquiry.email}?`)) return;
    try {
      const token = localStorage.getItem('adminToken');
      await axios.post(`${API}/api/admin/send-review-request/${enquiry.id}`,
        {},
        { headers: { Authorization: `Bearer ${token}` }}
      );
      toast.success(`Review request sent to ${enquiry.email}`);
      setEnquiries(enquiries.map(e =>
        e.id === enquiry.id ? { ...e, review_request_sent: true } : e
      ));
    } catch (error) {
      console.error('Review request error:', error);
      toast.error(error.response?.data?.detail || 'Failed to send review request');
    }
  };

  const bulkDeletePending = async () => {
    const pendingCount = enquiries.filter(e => e.status === 'pending').length;
    
    if (pendingCount === 0) {
      toast.error('No pending orders to delete');
      return;
    }

    if (!window.confirm(`⚠️ DELETE ${pendingCount} PENDING ORDERS?\n\nThis will permanently delete all orders with "Pending" status.\n\nAre you sure?`)) {
      return;
    }

    try {
      const token = localStorage.getItem('adminToken');
      const response = await axios.delete(`${API}/api/admin/enquiries/bulk/pending`, {
        headers: { Authorization: `Bearer ${token}` }
      });

      toast.success(`Deleted ${response.data.deleted_count} pending orders`);
      fetchEnquiries(); // Refresh list
    } catch (error) {
      console.error('Bulk delete error:', error);
      toast.error('Failed to delete pending orders');
    }
  };

  const bulkDeleteCompleted = async () => {
    const completedCount = enquiries.filter(e => e.status === 'completed').length;
    
    if (completedCount === 0) {
      toast.error('No completed orders to delete');
      return;
    }

    if (!window.confirm(`⚠️ DELETE ${completedCount} COMPLETED ORDERS?\n\nThis will permanently delete all orders with "Completed" status.\n\nAre you sure?`)) {
      return;
    }

    try {
      const token = localStorage.getItem('adminToken');
      const response = await axios.delete(`${API}/api/admin/enquiries/bulk/completed`, {
        headers: { Authorization: `Bearer ${token}` }
      });

      toast.success(`Deleted ${response.data.deleted_count} completed orders`);
      fetchEnquiries(); // Refresh list
    } catch (error) {
      console.error('Bulk delete error:', error);
      toast.error('Failed to delete completed orders');
    }
  };

  const bulkDeleteAll = async () => {
    if (enquiries.length === 0) {
      toast.error('No orders to delete');
      return;
    }

    if (!window.confirm(`🚨 DANGER: DELETE ALL ${enquiries.length} ORDERS?\n\nThis will PERMANENTLY delete EVERY order (pending, completed, all statuses).\n\nThis action CANNOT be undone!\n\nType "DELETE" in the next prompt to confirm.`)) {
      return;
    }

    const confirmation = window.prompt('Type DELETE in capital letters to confirm:');
    if (confirmation !== 'DELETE') {
      toast.error('Deletion cancelled - incorrect confirmation');
      return;
    }

    try {
      const token = localStorage.getItem('adminToken');
      const response = await axios.delete(`${API}/api/admin/enquiries/bulk/all`, {
        headers: { Authorization: `Bearer ${token}` }
      });

      toast.success(`Deleted ALL ${response.data.deleted_count} orders`);
      fetchEnquiries(); // Refresh list
    } catch (error) {
      console.error('Bulk delete all error:', error);
      toast.error('Failed to delete all orders');
    }
  };

  const getPaymentStatusBadge = (enquiry) => {
    const isPaidInFull = enquiry.payment_status === 'paid_in_full' || enquiry.final_payment_status === 'completed';
    
    if (isPaidInFull) {
      return (
        <span className="inline-flex items-center gap-1 px-3 py-1 rounded-full text-xs font-medium bg-green-500/20 text-green-400 border border-green-500/50">
          <CheckCircle size={12} />
          Paid in Full
        </span>
      );
    }
    
    return (
      <span className="inline-flex items-center gap-1 px-3 py-1 rounded-full text-xs font-medium bg-amber-500/20 text-amber-400 border border-amber-500/50">
        <DollarSign size={12} />
        Deposit Only
      </span>
    );
  };

  const filteredEnquiries = enquiries.filter(enquiry => {
    const matchesSearch = (enquiry.name?.toLowerCase() || '').includes(searchTerm.toLowerCase()) ||
                         (enquiry.email?.toLowerCase() || '').includes(searchTerm.toLowerCase());
    const matchesStatus = filterStatus === 'all' || enquiry.status === filterStatus;
    return matchesSearch && matchesStatus;
  });

  const getStatusColor = (status) => {
    const colors = {
      pending: 'bg-yellow-500/20 text-yellow-400 border-yellow-500/50',
      'in-progress': 'bg-blue-500/20 text-blue-400 border-blue-500/50',
      completed: 'bg-green-500/20 text-green-400 border-green-500/50'
    };
    return colors[status] || colors.pending;
  };

  const formatDate = (timestamp) => {
    if (!timestamp) return 'Unknown';
    const date = new Date(timestamp);
    return date.toLocaleDateString('en-GB', { 
      day: '2-digit', 
      month: 'short', 
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold text-white mb-2">Customer Enquiries</h1>
          <p className="text-gray-400">Manage and track all customer orders</p>
        </div>
        <div className="flex items-center gap-4">
          <button
            onClick={createTestOrder}
            className="bg-gradient-to-r from-purple-500 to-pink-500 text-white px-5 py-2.5 rounded-lg font-semibold hover:from-purple-600 hover:to-pink-600 transition-all shadow-lg hover:shadow-purple-500/50 flex items-center gap-2 text-sm"
            title="Creates a fake paid order - no Stripe charge - for testing uploads, emails and admin flow"
            data-testid="create-test-order-btn"
          >
            🧪 Create Test Order
          </button>
          <div className="text-right">
            <div className="text-3xl font-bold text-white">{enquiries.length}</div>
            <div className="text-sm text-gray-400">Total Orders</div>
          </div>
        </div>
      </div>

      {/* Filters */}
      <div className="bg-gradient-to-br from-gray-900 to-gray-800 rounded-2xl border border-gray-700 p-6">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {/* Search */}
          <div className="relative">
            <Search className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-400" size={20} />
            <input
              type="text"
              placeholder="Search by name or email..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="w-full pl-12 pr-4 py-3 bg-gray-800 text-white rounded-lg border border-gray-700 focus:border-cyan-500 focus:ring-2 focus:ring-cyan-500/50 outline-none"
            />
          </div>

          {/* Status Filter */}
          <select
            value={filterStatus}
            onChange={(e) => setFilterStatus(e.target.value)}
            className="px-4 py-3 bg-gray-800 text-white rounded-lg border border-gray-700 focus:border-cyan-500 focus:ring-2 focus:ring-cyan-500/50 outline-none"
          >
            <option value="all">All Status</option>
            <option value="pending">Pending</option>
            <option value="in-progress">In Progress</option>
            <option value="completed">Completed</option>
          </select>
        </div>
      </div>

      {/* Bulk Actions */}
      {enquiries.length > 0 && (
        <div className="bg-gradient-to-br from-gray-900 to-gray-800 rounded-2xl border border-gray-700 p-6">
          <h3 className="text-lg font-bold text-white mb-4">Bulk Actions</h3>
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
            <button
              onClick={bulkDeletePending}
              className="px-4 py-3 bg-yellow-600 text-white rounded-lg hover:bg-yellow-700 transition-all font-semibold flex items-center justify-center gap-2"
            >
              <Trash2 size={18} />
              Delete All Pending ({enquiries.filter(e => e.status === 'pending').length})
            </button>
            
            <button
              onClick={bulkDeleteCompleted}
              className="px-4 py-3 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-all font-semibold flex items-center justify-center gap-2"
            >
              <Trash2 size={18} />
              Delete All Completed ({enquiries.filter(e => e.status === 'completed').length})
            </button>

            <button
              onClick={bulkDeleteTestOrders}
              data-testid="delete-test-orders-btn"
              className="px-4 py-3 bg-purple-600 text-white rounded-lg hover:bg-purple-700 transition-all font-semibold flex items-center justify-center gap-2"
              title="Delete all test orders created via the 🧪 Create Test Order button"
            >
              <Trash2 size={18} />
              Delete Test Orders ({enquiries.filter(e => e.is_test).length})
            </button>
            
            <button
              onClick={bulkDeleteAll}
              className="px-4 py-3 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-all font-semibold flex items-center justify-center gap-2"
            >
              <Trash2 size={18} />
              ⚠️ Delete ALL ({enquiries.length})
            </button>
          </div>
          <p className="text-xs text-gray-400 mt-3 text-center">
            ⚠️ Bulk deletions are permanent and cannot be undone. Use with caution.
          </p>
        </div>
      )}

      {/* Enquiries Table */}
      <div className="bg-gradient-to-br from-gray-900 to-gray-800 rounded-2xl border border-gray-700 overflow-hidden">
        {loading ? (
          <div className="flex items-center justify-center py-20">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-amber-500"></div>
          </div>
        ) : filteredEnquiries.length === 0 ? (
          <div className="p-12 text-center">
            <Package size={48} className="text-gray-600 mx-auto mb-4" />
            <p className="text-gray-400 text-lg">
              {searchTerm || filterStatus !== 'all' 
                ? 'No enquiries match your filters' 
                : 'No enquiries yet. They will appear here once customers start ordering!'}
            </p>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-gray-800/50 border-b border-gray-700">
                <tr>
                  <th className="px-6 py-4 text-left text-sm font-semibold text-gray-300">Customer</th>
                  <th className="px-6 py-4 text-left text-sm font-semibold text-gray-300">Package</th>
                  <th className="px-6 py-4 text-left text-sm font-semibold text-gray-300">Payment</th>
                  <th className="px-6 py-4 text-left text-sm font-semibold text-gray-300">Date</th>
                  <th className="px-6 py-4 text-left text-sm font-semibold text-gray-300">Status</th>
                  <th className="px-6 py-4 text-left text-sm font-semibold text-gray-300">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-700">
                {filteredEnquiries.map((enquiry) => (
                  <tr key={enquiry.id} className="hover:bg-gray-800/30 transition-colors">
                    <td className="px-6 py-4">
                      <div>
                        <div className="text-white font-medium flex items-center gap-2">
                          {enquiry.name}
                          {enquiry.is_test && (
                            <span className="px-2 py-0.5 text-[10px] font-bold rounded-full bg-purple-500/20 text-purple-300 border border-purple-500/50">
                              TEST
                            </span>
                          )}
                        </div>
                        <div className="text-sm text-gray-400 flex items-center gap-1 mt-1">
                          <Mail size={12} />
                          {enquiry.email}
                        </div>
                        {enquiry.phone && (
                          <div className="text-sm text-gray-400 flex items-center gap-1 mt-1">
                            <Phone size={12} />
                            {enquiry.phone}
                          </div>
                        )}
                      </div>
                    </td>
                    <td className="px-6 py-4">
                      <div className="text-white">{enquiry.packageName || enquiry.slideshowType || 'N/A'}</div>
                      {enquiry.estimatedPrice && (
                        <div className="text-sm text-gray-400">£{enquiry.estimatedPrice.toFixed(2)}</div>
                      )}
                    </td>
                    <td className="px-6 py-4">
                      {getPaymentStatusBadge(enquiry)}
                    </td>
                    <td className="px-6 py-4">
                      <div className="text-sm text-gray-300">{formatDate(enquiry.timestamp)}</div>
                    </td>
                    <td className="px-6 py-4">
                      <select
                        value={enquiry.status || 'pending'}
                        onChange={(e) => handleStatusChange(enquiry.id, e.target.value)}
                        className={`px-3 py-1.5 rounded-full text-xs font-medium border ${getStatusColor(enquiry.status || 'pending')} bg-transparent cursor-pointer`}
                      >
                        <option value="pending">Pending</option>
                        <option value="in-progress">In Progress</option>
                        <option value="completed">Completed</option>
                      </select>
                    </td>
                    <td className="px-6 py-4">
                      <div className="flex items-center gap-2">
                        <button
                          onClick={() => setSelectedEnquiry(enquiry)}
                          className="p-2 bg-cyan-600 text-white rounded-lg hover:bg-cyan-700 transition-all"
                          title="View Details"
                        >
                          <Eye size={16} />
                        </button>
                        <button
                          onClick={() => handleDelete(enquiry.id)}
                          className="p-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-all"
                          title="Delete"
                        >
                          <Trash2 size={16} />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Enquiry Details Modal */}
      {selectedEnquiry && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-gradient-to-br from-gray-900 to-gray-800 rounded-2xl border border-gray-700 max-w-2xl w-full max-h-[90vh] overflow-y-auto">
            <div className="sticky top-0 bg-gray-900 border-b border-gray-700 p-6 flex items-center justify-between">
              <h2 className="text-2xl font-bold text-white">Order Details</h2>
              <button
                onClick={() => setSelectedEnquiry(null)}
                className="p-2 hover:bg-gray-800 rounded-lg transition-colors"
              >
                <X size={24} className="text-gray-400" />
              </button>
            </div>

            <div className="p-6 space-y-6">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <div className="text-sm text-gray-400 mb-1">Customer Name</div>
                  <div className="text-white font-medium">{selectedEnquiry.name}</div>
                </div>
                <div>
                  <div className="text-sm text-gray-400 mb-1">Email</div>
                  <div className="text-white">{selectedEnquiry.email}</div>
                </div>
                <div>
                  <div className="text-sm text-gray-400 mb-1">Phone</div>
                  <div className="text-white">{selectedEnquiry.phone || 'N/A'}</div>
                </div>
                <div>
                  <div className="text-sm text-gray-400 mb-1">Order Date</div>
                  <div className="text-white">{formatDate(selectedEnquiry.timestamp)}</div>
                </div>
                <div>
                  <div className="text-sm text-gray-400 mb-1">Package</div>
                  <div className="text-white">{selectedEnquiry.packageName || selectedEnquiry.slideshowType || 'N/A'}</div>
                </div>
                <div>
                  <div className="text-sm text-gray-400 mb-1">Total Price</div>
                  <div className="text-white font-bold">£{selectedEnquiry.estimatedPrice?.toFixed(2) || 'N/A'}</div>
                </div>
                <div>
                  <div className="text-sm text-gray-400 mb-1">Order ID</div>
                  <div className="text-white font-mono text-sm">{selectedEnquiry.orderId || selectedEnquiry.id}</div>
                </div>
                <div>
                  <div className="text-sm text-gray-400 mb-1">Status</div>
                  <span className={`inline-block px-3 py-1 rounded-full text-xs font-medium border ${getStatusColor(selectedEnquiry.status || 'pending')}`}>
                    {selectedEnquiry.status || 'pending'}
                  </span>
                </div>
              </div>

              {(selectedEnquiry.addons || []).length > 0 && (
                <div data-testid="admin-order-addons">
                  <div className="text-sm text-gray-400 mb-2">Add-ons</div>
                  <div className="bg-gray-800/50 rounded-lg p-4 space-y-1 text-sm">
                    {selectedEnquiry.package_price != null && (
                      <div className="flex justify-between text-gray-400"><span>Package price</span><span>£{Number(selectedEnquiry.package_price).toFixed(2)}</span></div>
                    )}
                    {selectedEnquiry.addons.map((a) => (
                      <div key={a.id} className="flex justify-between text-gray-200">
                        <span>{a.name}{a.quantity > 1 ? ` (${a.quantity})` : ''}</span>
                        <span>+£{Number(a.price).toFixed(2)}</span>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {selectedEnquiry.personalisedSong && (
                <div data-testid="admin-song-details">
                  <div className="text-sm text-amber-400 mb-2">Personalised Song Details</div>
                  <div className="bg-gray-800/50 border border-amber-500/20 rounded-lg p-4 grid md:grid-cols-2 gap-3 text-sm">
                    {[['names', 'Names'], ['occasion', 'Occasion'], ['genre', 'Genre'], ['mood', 'Mood'], ['language', 'Language'], ['memories', 'Special memories']].map(([k, label]) => (
                      <div key={k} className={k === 'memories' ? 'md:col-span-2' : ''}>
                        <div className="text-gray-500 text-xs">{label}</div>
                        <div className="text-gray-200 whitespace-pre-line">{selectedEnquiry.songDetails?.[k] || '-'}</div>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {selectedEnquiry.message && (
                <div>
                  <div className="text-sm text-gray-400 mb-2">Customer Message</div>
                  <div className="bg-gray-800/50 rounded-lg p-4 text-gray-300">
                    {selectedEnquiry.message}
                  </div>
                </div>
              )}

              {/* USB Delivery Address (collected at Stripe checkout) */}
              <div className="border-t border-gray-700 pt-6" data-testid="admin-delivery-address">
                <div className="flex items-center justify-between mb-3">
                  <h3 className="text-lg font-bold text-white flex items-center gap-2">
                    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="text-amber-400"><path d="M20 10c0 6-8 12-8 12s-8-6-8-12a8 8 0 0 1 16 0z"/><circle cx="12" cy="10" r="3"/></svg>
                    USB Delivery Address
                  </h3>
                  {selectedEnquiry.delivery_address && (
                    <button
                      type="button"
                      onClick={() => {
                        const a = selectedEnquiry.delivery_address;
                        const lines = [a.name, a.line1, a.line2, a.city, a.state, a.postal_code, a.country].filter(Boolean).join('\n');
                        navigator.clipboard.writeText(lines).then(() => toast.success('Address copied'));
                      }}
                      className="text-xs px-3 py-1.5 rounded-md border border-gray-600 text-gray-300 hover:border-amber-500/50 hover:text-amber-300"
                    >
                      Copy
                    </button>
                  )}
                </div>
                {selectedEnquiry.delivery_address ? (
                  <div className="bg-gray-800/50 rounded-lg p-4 text-gray-200 text-sm space-y-0.5">
                    <div className="font-semibold text-white">{selectedEnquiry.delivery_address.name}</div>
                    {selectedEnquiry.delivery_address.line1 && <div>{selectedEnquiry.delivery_address.line1}</div>}
                    {selectedEnquiry.delivery_address.line2 && <div>{selectedEnquiry.delivery_address.line2}</div>}
                    <div>
                      {[selectedEnquiry.delivery_address.city, selectedEnquiry.delivery_address.state, selectedEnquiry.delivery_address.postal_code].filter(Boolean).join(', ')}
                    </div>
                    {selectedEnquiry.delivery_address.country && <div className="text-gray-400">{selectedEnquiry.delivery_address.country}</div>}
                    {selectedEnquiry.delivery_address.phone && (
                      <div className="pt-2 text-gray-400 text-xs">
                        Phone: <span className="text-gray-200">{selectedEnquiry.delivery_address.phone}</span>
                      </div>
                    )}
                  </div>
                ) : (
                  <div className="bg-gray-800/40 border border-dashed border-gray-700 rounded-lg p-4 text-sm text-gray-500">
                    Address not yet captured. Stripe will collect it during checkout and it will appear here once the deposit is confirmed.
                  </div>
                )}
              </div>

              {/* Production Stage Control */}
              <div className="border-t border-gray-700 pt-6">
                <h3 className="text-lg font-bold text-white mb-4">Production Stage <span className="text-xs text-gray-500 font-normal">(customer-facing)</span></h3>
                <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
                  {[
                    { v: 0, label: 'Order Received' },
                    { v: 1, label: 'In Production' },
                    { v: 2, label: 'Final Review' },
                    { v: 3, label: 'Ready' },
                  ].map((s) => {
                    const current = Number(selectedEnquiry.production_stage || 0);
                    const isActive = current === s.v;
                    return (
                      <button
                        key={s.v}
                        onClick={() => handleStageChange(selectedEnquiry.id, s.v)}
                        data-testid={`admin-stage-btn-${s.v}`}
                        className={`px-3 py-2.5 rounded-lg text-xs font-medium border transition-all ${
                          isActive
                            ? 'bg-amber-500 border-amber-500 text-black'
                            : 'bg-gray-800/40 border-gray-700 text-gray-300 hover:border-amber-500/40 hover:text-amber-300'
                        }`}
                      >
                        {s.v + 1}. {s.label}
                      </button>
                    );
                  })}
                </div>
                <p className="text-xs text-gray-500 mt-3">
                  The customer sees this on their order status page ({window.location.origin}/order/{selectedEnquiry.orderId || selectedEnquiry.id}).
                </p>
              </div>

              {/* Payment Management Section */}
              <div className="border-t border-gray-700 pt-6">
                <h3 className="text-lg font-bold text-white mb-4">Payment Management</h3>
                
                <div className="grid grid-cols-2 gap-4 mb-4">
                  <div className="bg-gray-800/50 rounded-lg p-4">
                    <div className="text-sm text-gray-400 mb-1">Total Price</div>
                    <div className="text-2xl font-bold text-white">£{selectedEnquiry.estimatedPrice?.toFixed(2) || '0.00'}</div>
                  </div>
                  <div className="bg-gray-800/50 rounded-lg p-4">
                    <div className="text-sm text-gray-400 mb-1">Deposit Paid</div>
                    <div className="text-2xl font-bold text-green-400">£{(selectedEnquiry.depositAmount ?? selectedEnquiry.deposit_amount ?? ((selectedEnquiry.estimatedPrice || 0) * 0.25)).toFixed(2)}</div>
                  </div>
                  <div className="bg-gray-800/50 rounded-lg p-4">
                    <div className="text-sm text-gray-400 mb-1">Remaining Balance</div>
                    <div className="text-2xl font-bold text-amber-400">£{Math.max((selectedEnquiry.estimatedPrice || 0) - (selectedEnquiry.depositAmount ?? selectedEnquiry.deposit_amount ?? ((selectedEnquiry.estimatedPrice || 0) * 0.25)), 0).toFixed(2)}</div>
                  </div>
                  <div className="bg-gray-800/50 rounded-lg p-4">
                    <div className="text-sm text-gray-400 mb-1">Payment Status</div>
                    <div className="mt-2">{getPaymentStatusBadge(selectedEnquiry)}</div>
                  </div>
                </div>

                {selectedEnquiry.payment_status !== 'paid_in_full' && selectedEnquiry.final_payment_status !== 'completed' && (
                  <div className="space-y-3">
                    {selectedEnquiry.final_payment_link ? (
                      <div className="bg-cyan-900/20 border border-cyan-500/30 rounded-lg p-4">
                        <div className="text-sm text-cyan-400 mb-2">✅ Payment link created & email sent!</div>
                        <div className="flex gap-2 mb-3">
                          <input
                            type="text"
                            value={selectedEnquiry.final_payment_link}
                            readOnly
                            className="flex-1 px-3 py-2 bg-gray-800 text-gray-300 text-sm rounded border border-gray-700"
                          />
                          <button
                            onClick={() => {
                              navigator.clipboard.writeText(selectedEnquiry.final_payment_link);
                              toast.success('Link copied!');
                            }}
                            className="px-4 py-2 bg-cyan-600 text-white rounded hover:bg-cyan-700 transition-all text-sm"
                          >
                            Copy Link
                          </button>
                        </div>
                        <button
                          onClick={() => resendPaymentEmail(selectedEnquiry)}
                          className="w-full px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700 transition-all text-sm flex items-center justify-center gap-2"
                        >
                          <Mail size={16} />
                          Resend Email to {selectedEnquiry.email}
                        </button>
                        <p className="text-xs text-gray-400 mt-2">
                          Customer received an automated email with payment instructions and link.
                        </p>
                      </div>
                    ) : (
                      <button
                        onClick={() => createFinalPaymentLink(selectedEnquiry)}
                        className="w-full inline-flex items-center justify-center gap-2 px-6 py-3 bg-gradient-to-r from-amber-500 to-amber-600 text-black font-semibold rounded-lg hover:from-amber-600 hover:to-amber-700 transition-all"
                      >
                        <LinkIcon size={18} />
                        Create Payment Link & Send Email
                      </button>
                    )}

                    <button
                      onClick={() => markAsPaid(selectedEnquiry.id)}
                      className="w-full inline-flex items-center justify-center gap-2 px-6 py-3 bg-green-600 text-white font-semibold rounded-lg hover:bg-green-700 transition-all"
                    >
                      <CheckCircle size={18} />
                      Mark as Paid in Full (Manual)
                    </button>

                    <p className="text-xs text-gray-400 text-center">
                      Use "Mark as Paid" if customer paid via bank transfer or cash
                    </p>
                  </div>
                )}

                {(selectedEnquiry.payment_status === 'paid_in_full' || selectedEnquiry.final_payment_status === 'completed') && (
                  <div className="space-y-3">
                    <div className="bg-green-900/20 border border-green-500/30 rounded-lg p-4 text-center">
                      <CheckCircle size={32} className="text-green-400 mx-auto mb-2" />
                      <p className="text-green-400 font-semibold">Payment Complete!</p>
                      <p className="text-sm text-gray-400 mt-1">Customer has paid in full</p>
                    </div>

                    <button
                      onClick={() => sendReviewRequest(selectedEnquiry)}
                      data-testid="send-review-request-btn"
                      className="w-full inline-flex items-center justify-center gap-2 px-6 py-3 bg-gradient-to-r from-purple-500 to-pink-500 text-white font-semibold rounded-lg hover:from-purple-600 hover:to-pink-600 transition-all"
                    >
                      <Mail size={18} />
                      {selectedEnquiry.review_request_sent ? 'Resend Review Request' : 'Send Review Request'}
                    </button>
                    {selectedEnquiry.review_request_sent && (
                      <p className="text-xs text-gray-400 text-center">
                        Review request was already sent. Click to resend.
                      </p>
                    )}
                  </div>
                )}
              </div>

              {selectedEnquiry.orderId && (
                <div>
                  <div className="text-sm text-gray-400 mb-2">Customer Files</div>
                  <a
                    href={`/admin/files?order=${selectedEnquiry.orderId}`}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="inline-flex items-center gap-2 px-4 py-2 bg-amber-600 text-white rounded-lg hover:bg-amber-700 transition-all"
                    data-testid="view-files-btn"
                  >
                    <Download size={16} />
                    View & Download Files
                  </a>
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AdminEnquiries;
